import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Activity, getActivityList } from "../../apis/activityApis";

/* ================= STATE ================= */

interface ActivityState {
  activityList: Activity[];
  loading: boolean;
  error: string | null;
}

const initialState: ActivityState = {
  activityList: [],
  loading: false,
  error: null,
};

/* ================= THUNKS ================= */

export const fetchActivityListThunk = createAsyncThunk<
  Activity[],
  void,
  { rejectValue: string }
>("activity/fetchList", async (_, { rejectWithValue }) => {
  try {
    const response = await getActivityList();

    if (!response.status) {
      return rejectWithValue("Failed to fetch activities");
    }

    return response.data;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch activities",
    );
  }
});

/* ================= SLICE ================= */

const activitySlice = createSlice({
  name: "activity",
  initialState,
  reducers: {
    clearActivityState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActivityListThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActivityListThunk.fulfilled, (state, action) => {
        state.loading = false;

        // sort by sortOrder
        state.activityList = action.payload.sort(
          (a, b) => a.sortOrder - b.sortOrder,
        );
      })
      .addCase(fetchActivityListThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Something went wrong";
      });
  },
});

/* ================= EXPORTS ================= */

export const { clearActivityState } = activitySlice.actions;
export default activitySlice.reducer;
